/**
 * @class LoadPageBatch
 * @classdesc Load page batch based on the liquid pagination object.
 * @extends HTMLElement
 */
class LoadPageBatch extends HTMLElement {
  sectionId = this.getAttribute('id');
  pages = parseInt(this.getAttribute('pages'));
  currentPage = parseInt(this.getAttribute('current-page'));
  pageSize = parseInt(this.getAttribute('page-size'));

  connectedCallback() {
    this.pagination = this.querySelector('[data-batch="pagination"]');
    this.buttons = this.querySelectorAll('[data-batch-direction]');
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.handleBatch(button));
    });

    window.onpopstate = () => {
      this.handleBatch();
    };
  }

  loading(state = true) {
    state ? this.setAttribute('loading', '') : this.removeAttribute('loading');
  }

  /**
   * @method handleSearchParams
   * @description Handle the search params for the next or previous page.
   * @returns {void}
   */
  handleSearchParams() {
    if (!this.direction) return;
    this.searchParams = new URLSearchParams(document.location.search);

    let theNewPage = this.currentPage;
    if (this.direction === 'next') {
      theNewPage = parseInt(this.getAttribute('page-next')) + 1;
    } else if (this.direction === 'previous') {
      theNewPage = parseInt(this.getAttribute('page-previous')) - 1;
    }

    // New page must be in pagination range, stop if not
    if (theNewPage > this.pages && theNewPage < 1) return;
    this.newPage = theNewPage;

    this.searchParams.set('page', this.newPage);
    this.searchParams.delete('section_id');
    this.clientUrl = document.location.pathname + '?' + this.searchParams.toString();

    this.searchParams.set('section_id', this.sectionId);
    this.fetchUrl = document.location.pathname + '?' + this.searchParams.toString();
  }

  /**
   * @async
   * @method handleBatch
   * @param {HTMLElement} button - The button element.
   * @returns {void}
   */
  async handleBatch(button) {
    this.direction = button?.dataset?.batchDirection || 'current';
    this.loading();
    this.handleSearchParams();
    try {
      const html = await this.getHtml();
      this.render(html);
      this.pagination.innerHTML = this.newPagination.innerHTML || this.pagination.innerHTML;
      window.history.pushState({}, '', this.clientUrl);

      button?.dataset?.batchDirection === 'next'
        ? this.setAttribute('page-next', this.newPage)
        : this.setAttribute('page-previous', this.newPage);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading(false);
    }
  }

  /**
   * @async
   * @method getHtml
   * @description Get the HTML content from the fetch URL.
   * @returns {string} The HTML content.
   */
  async getHtml() {
    const response = await fetch(this.fetchUrl);

    if (!response.ok) {
      throw new Error(response);
    }
    const text = await response.text();

    const html = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('[data-batch="list"]').innerHTML;

    this.newPagination = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('[data-batch="pagination"]');

    return html;
  }

  /**
   * @method render
   * @description Render the HTML content.
   * @param {string} html - The HTML content.
   * @returns {void}
   */
  render(html) {
    this.list = this.querySelector('[data-batch="list"]');

    if (!html || !this.list) return;

    // Add new content before or after the current content
    if (this.direction === 'next') {
      this.list.insertAdjacentHTML('beforeend', html);
    } else if (this.direction === 'previous') {
      this.list.insertAdjacentHTML('afterbegin', html);
      this.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    } else {
      this.list.innerHTML = html;
    }
    this.currentPage = this.newPage;
    this.setAttribute('current-page', this.currentPage);

    this.currentPage === 1
      ? this.querySelector('[data-batch-direction="previous"]').classList.add('hidden')
      : null;

    this.currentPage === this.pages
      ? this.querySelector('[data-batch-direction="next"]').classList.add('hidden')
      : null;

    // this.pagination;

    // Update the optional items viewed count
    const itemsViewed = this.querySelector('[data-batch="items-viewed"]');
    const countItems = this.querySelectorAll('[data-batch="item"]').length || null;
    if (!itemsViewed || !countItems) return;
    itemsViewed.textContent = countItems;
  }
}

customElements.define('load-page-batch', LoadPageBatch);
